import { useEffect, useMemo, useState } from 'react'

import { useAuthContext } from '@/common/components/AuthProvider/AuthContext'
import { AUTH_PROVIDERS, useAuth } from '@/common/hooks/useAuth'
import * as AuthorisationUtils from '@/common/utils/Authorisation/AuthorisationUtils'

/**
 * useSitePermissions - Site-level permission checking for dual-provider support
 *
 * Handles both MSAL (Azure AD B2C) and Kinde authentication providers during
 * the migration period. Returns boolean flags for site-level admin permissions.
 *
 * For MSAL users: Checks idTokenClaims.extension_UserRole custom claim (synchronous)
 * For Kinde users: Uses async getPermission() from Kinde SDK
 *
 * Note: This is for SITE-LEVEL permissions (SiteAdmin, SiteSupport).
 * For ORGANIZATION-LEVEL roles (owner, administrator, user), use RoleRequiredContainer
 * or the selectUserRole Redux selector.
 *
 * @returns {{ isSiteAdmin: boolean, isSiteAdminOrSupport: boolean, isLoading: boolean }}
 *
 * @example
 * // Check if user can access admin-only features
 * const { isSiteAdmin, isLoading } = useSitePermissions()
 * if (isLoading) return <Loading />
 * if (isSiteAdmin) {
 *   // Show admin-only content
 * }
 *
 * @example
 * // Check if user can access admin or support features
 * const { isSiteAdminOrSupport } = useSitePermissions()
 * const showAdministrationLink = isSiteAdminOrSupport
 */
export const useSitePermissions = () => {
    const { getPermission, provider } = useAuth()
    const { user } = useAuthContext()

    // State for Kinde async permissions
    const [kindePermissions, setKindePermissions] = useState({
        isSiteAdmin: false,
        isSiteAdminOrSupport: false,
        isLoading: true,
    })

    // Fetch Kinde permissions asynchronously
    useEffect(() => {
        if (provider !== AUTH_PROVIDERS.KINDE) {
            return
        }

        let isMounted = true

        const fetchPermissions = async () => {
            try {
                const [adminResult, supportResult] = await Promise.all([
                    getPermission('admin:site'),
                    getPermission('support:site'),
                ])

                if (isMounted) {
                    const hasAdmin = adminResult?.isGranted === true
                    const hasSupport = supportResult?.isGranted === true
                    setKindePermissions({
                        isSiteAdmin: hasAdmin,
                        isSiteAdminOrSupport: hasAdmin || hasSupport,
                        isLoading: false,
                    })
                }
            } catch (error) {
                console.error('useSitePermissions: Error fetching Kinde permissions', error)
                if (isMounted) {
                    setKindePermissions({
                        isSiteAdmin: false,
                        isSiteAdminOrSupport: false,
                        isLoading: false,
                    })
                }
            }
        }

        fetchPermissions()

        return () => {
            isMounted = false
        }
    }, [provider, getPermission])

    /**
     * For MSAL, compute permissions synchronously from user claims.
     * For Kinde, use the async state.
     */
    const permissions = useMemo(() => {
        if (provider === AUTH_PROVIDERS.MSAL) {
            return {
                isSiteAdmin: AuthorisationUtils.isSiteAdmin(user),
                isSiteAdminOrSupport: AuthorisationUtils.isSiteAdminOrSupport(user),
                isLoading: false,
            }
        }

        // For Kinde, return the async state
        return kindePermissions
    }, [provider, user, kindePermissions])

    return permissions
}
